import {
  getEndTimeFromStartAndDurationSeconds,
  resolveSelectionIndex,
} from 'features/Responder/features/Schedules/features/ScheduleBuilder/utils';
import { useEffect } from 'react';
import { RRule } from 'rrule';

export const useScheduleBuilderEditHydration = ({
  activeEditSchedule,
  tours,
  zones,
  selectedTourIdRef,
  selectedZoneIdRef,
  setSelectedTour,
  setSelectedZone,
  selectTour,
  focusTourOnMap,
  setActiveZoneId,
  setZoneSelectedZoneIds,
  setMapSelectedZoneIds,
  setSelectedTourIds,
  setRrule,
  setStartTime,
  setOldScheduleDuration,
  setOldTimeSlot,
  setEndTime,
  getTotalDurationOfTour,
  setUseTourDuration,
  setSelectedPersonnel,
  setNotes,
  setScheduleName,
  setColor,
}) => {
  useEffect(() => {
    if (activeEditSchedule) {
      const isZoneSchedule = activeEditSchedule.entity.type === 'Zone';
      const nextTourIndex = isZoneSchedule
        ? 0
        : resolveSelectionIndex(tours, activeEditSchedule.entity.entity_id);
      const nextZoneIndex = isZoneSchedule
        ? resolveSelectionIndex(zones, activeEditSchedule.entity.entity_id)
        : 0;
      selectedTourIdRef.current =
        !isZoneSchedule && nextTourIndex > 0
          ? (tours[nextTourIndex - 1]?.id ?? activeEditSchedule.entity.entity_id)
          : null;
      selectedZoneIdRef.current =
        isZoneSchedule && nextZoneIndex > 0
          ? (zones[nextZoneIndex - 1]?.id ?? activeEditSchedule.entity.entity_id)
          : null;
      setSelectedTour(nextTourIndex);
      setSelectedZone(nextZoneIndex);
      if (isZoneSchedule) {
        const zoneId = nextZoneIndex
          ? (zones[nextZoneIndex - 1]?.id ?? activeEditSchedule.entity.entity_id)
          : activeEditSchedule.entity.entity_id;
        selectTour(null);
        setActiveZoneId(zoneId ?? null);
        setZoneSelectedZoneIds(zoneId != null ? [zoneId] : []);
        setMapSelectedZoneIds(zoneId != null ? [zoneId] : []);
        setSelectedTourIds([]);
      } else {
        const tourObj = nextTourIndex > 0 ? tours[nextTourIndex - 1] : null;
        selectTour(tourObj || null);
        focusTourOnMap(tourObj);
        setActiveZoneId(null);
        setZoneSelectedZoneIds([]);
        setMapSelectedZoneIds([]);
        setSelectedTourIds(tourObj?.id != null ? [tourObj.id] : []);
      }

      let hydratedStart = '';
      let hydratedEnd = '';
      let R;
      if (activeEditSchedule.recurrence) {
        console.log(
          'Parsing RRule from schedule recurrence for editing:',
          activeEditSchedule.recurrence,
        );
        R = RRule.fromString(activeEditSchedule.recurrence + ';BYSECOND=0');
        if (R) {
          setRrule(R);
        }
        let hour, minute;
        if (Array.isArray(R.options.byhour) && R.options.byhour.length > 0 && R.options.byminute) {
          const startTimes = [];
          const len = R.options.byhour.length;
          for (let i = 0; i < len; i++) {
            const hourStr = String(R.options.byhour[i]).padStart(2, '0');
            const minStr = String(R.options.byminute[0]).padStart(2, '0');
            startTimes.push(`${hourStr}:${minStr}`);
          }
          hydratedStart = startTimes[0];
          setStartTime(startTimes);
        } else if (R.options.dtstart && R.options.tzid) {
          const utcDate = R.options.dtstart;
          const tz = R.options.tzid;
          try {
            const fmt = new Intl.DateTimeFormat('en-US', {
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
              timeZone: tz,
            });
            const parts = fmt.formatToParts(utcDate);
            hour = parts.find((p) => p.type === 'hour')?.value;
            minute = parts.find((p) => p.type === 'minute')?.value;
            if (hour !== undefined && minute !== undefined) {
              hydratedStart = `${hour}:${minute}`;
            }
          } catch (e) {
            hour = String(utcDate.getUTCHours()).padStart(2, '0');
            minute = String(utcDate.getUTCMinutes()).padStart(2, '0');
            hydratedStart = `${hour}:${minute}`;
          }
        }

        if (hydratedStart && activeEditSchedule.duration) {
          hydratedEnd = getEndTimeFromStartAndDurationSeconds(
            hydratedStart,
            activeEditSchedule.duration / 1000,
          );
        }
      }

      if (R) {
        if (
          !(
            Array.isArray(R.options.byhour) &&
            R.options.byhour.length > 0 &&
            Array.isArray(R.options.byminute) &&
            R.options.byminute.length > 0
          )
        ) {
          setStartTime([hydratedStart]);
        }
      }
      setOldScheduleDuration(activeEditSchedule.duration / 60000 || 0);
      setOldTimeSlot({ start: hydratedStart, end: hydratedEnd });
      setEndTime([hydratedEnd]);

      const tourObj = nextTourIndex > 0 ? tours[nextTourIndex - 1] : null;
      const tourDurationMin = tourObj ? Math.ceil(getTotalDurationOfTour(tourObj) / 60) : 0;
      if (
        tourDurationMin > 0 &&
        Math.ceil(activeEditSchedule.duration / 60000) === tourDurationMin
      ) {
        setUseTourDuration(true);
      } else {
        setUseTourDuration(false);
      }

      setSelectedPersonnel(activeEditSchedule.personnel_ids || []);
      setNotes(activeEditSchedule.notes || '');
      setScheduleName(activeEditSchedule.name || '');
      setColor(activeEditSchedule.color || null);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeEditSchedule, tours, zones]);
};
